import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Plus,
  ArrowUpRight,
  Folder,
  Globe,
  Clock,
  Github,
  Layout,
  Code2,
  Loader2,
  Send,
  Activity,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { apiClient } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useWebSocket } from '@/hooks/useWebSocket';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/utils/cn';
import { toast } from 'sonner';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isConnected } = useWebSocket();
  const [aiPrompt, setAiPrompt] = useState('');

  // Fetch dashboard stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => apiClient.getDashboardStats(),
    refetchInterval: 30000,
  });

  // Fetch recent applications
  const { data: applications, isLoading: appsLoading } = useQuery({
    queryKey: ['recent-applications'],
    queryFn: () => apiClient.getApplications({ limit: 8 }),
  });

  // AI project creation
  const createProjectMutation = useMutation({
    mutationFn: async (prompt: string) => {
      // For now, create a blank project and navigate to IDE where agent can scaffold
      const app = await apiClient.createApplication({
        name: prompt.slice(0, 50),
        description: prompt,
        templateId: 'blank',
      });
      return app;
    },
    onSuccess: (app) => {
      navigate(`/builder/${app.id}`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create project');
    },
  });

  const handleAiSubmit = () => {
    const trimmed = aiPrompt.trim();
    if (!trimmed) return;
    createProjectMutation.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAiSubmit();
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
      case 'deployed':
      case 'DEPLOYED':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'running':
      case 'building':
        return <Activity className="h-4 w-4 text-blue-500 animate-pulse" />;
      case 'failed':
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
  };

  const greeting = getGreeting();

  return (
    <div className="container mx-auto p-6 space-y-8 max-w-6xl">
      {/* Hero — AI Project Creation */}
      <div className="relative rounded-xl border bg-gradient-to-br from-violet-500/10 via-background to-blue-500/10 p-8">
        <div className="max-w-2xl">
          <h1 className="text-3xl font-bold mb-1">
            {greeting}, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-muted-foreground mb-6">
            What would you like to build today?
          </p>

          <div className="flex items-end gap-2">
            <div className="flex-1 relative">
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe your project... e.g. 'Build a landing page for a SaaS product with pricing tables and a contact form'"
                className="w-full resize-none bg-background rounded-lg px-4 py-3 pr-12 text-sm min-h-[48px] max-h-[120px] border focus:outline-none focus:ring-2 focus:ring-primary/50"
                rows={1}
                disabled={createProjectMutation.isPending}
              />
              <div className="absolute right-2 bottom-2">
                <Sparkles className="h-4 w-4 text-violet-400" />
              </div>
            </div>
            <Button
              size="lg"
              onClick={handleAiSubmit}
              disabled={!aiPrompt.trim() || createProjectMutation.isPending}
              className="shrink-0"
            >
              {createProjectMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Connection status indicator */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <div className={cn(
            "h-2 w-2 rounded-full",
            isConnected ? "bg-green-500" : "bg-red-500"
          )} />
          <span className="text-xs text-muted-foreground">
            {isConnected ? 'Connected' : 'Offline'}
          </span>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          className="flex items-center gap-3 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-left"
          onClick={() => navigate('/applications')}
        >
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Plus className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-medium text-sm">Start from Scratch</p>
            <p className="text-xs text-muted-foreground">Pick a template or blank project</p>
          </div>
        </button>

        <button
          className="flex items-center gap-3 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-left"
          onClick={() => toast.info('GitHub import coming soon')}
        >
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
            <Github className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-sm">Import from GitHub</p>
            <p className="text-xs text-muted-foreground">Clone an existing repository</p>
          </div>
        </button>

        <button
          className="flex items-center gap-3 p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-left"
          onClick={() => navigate('/applications')}
        >
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
            <Layout className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-sm">Browse Templates</p>
            <p className="text-xs text-muted-foreground">Start from a pre-built template</p>
          </div>
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Projects"
          value={stats?.totalApplications || 0}
          loading={statsLoading}
          icon={<Folder className="h-4 w-4" />}
        />
        <StatCard
          label="Deployed"
          value={stats?.activeDeployments || 0}
          loading={statsLoading}
          icon={<Globe className="h-4 w-4" />}
        />
        <StatCard
          label="Build Success"
          value={`${stats?.buildSuccess || 95}%`}
          loading={statsLoading}
          icon={<Code2 className="h-4 w-4" />}
        />
        <StatCard
          label="API Calls"
          value={stats?.apiUsage || 0}
          loading={statsLoading}
          icon={<Activity className="h-4 w-4" />}
        />
      </div>

      {/* Recent Projects Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Projects</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/applications')}>
            View all <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>

        {appsLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-[140px] rounded-lg" />
            ))}
          </div>
        ) : applications?.applications?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {applications.applications.slice(0, 8).map((app) => (
              <div
                key={app.id}
                className="group relative rounded-lg border bg-card p-4 hover:border-primary/50 hover:shadow-sm cursor-pointer transition-all"
                onClick={() => navigate(`/builder/${app.id}`)}
              >
                {/* Preview thumbnail placeholder */}
                <div className="h-16 rounded-md bg-muted/50 mb-3 flex items-center justify-center overflow-hidden">
                  <Code2 className="h-6 w-6 text-muted-foreground/50" />
                </div>

                <p className="font-medium text-sm truncate">{app.name}</p>
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {app.description || 'No description'}
                </p>

                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center gap-1.5">
                    {getStatusIcon(app.status)}
                    <span className="text-xs text-muted-foreground capitalize">
                      {app.status?.toLowerCase()}
                    </span>
                  </div>
                  <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Sparkles className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="font-medium mb-1">No projects yet</p>
              <p className="text-sm text-muted-foreground mb-4">
                Describe what you want to build above, or start from a template.
              </p>
              <Button variant="outline" onClick={() => navigate('/applications')}>
                <Plus className="h-4 w-4 mr-2" /> Create Project
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ── Helpers ──

function StatCard({
  label,
  value,
  loading,
  icon,
}: {
  label: string;
  value: string | number;
  loading: boolean;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-muted-foreground">{icon}</span>
      </div>
      {loading ? (
        <Skeleton className="h-7 w-16" />
      ) : (
        <p className="text-xl font-bold">{value}</p>
      )}
    </div>
  );
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}
